import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { isEmpty, isNull } from 'lodash';

import type { ClaimChatFeatures } from '~/components/AiChat/controller/types';
import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import { ChatContextDocumentsDialog } from '~/components/AiChat/SideBarDialog/ChatContextDocumentsDialog/ChatContextDocumentsDialog';
import { useClaim } from '~/components/ClaimContainer';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import ListItemText from '~/components/core/Atomic/ListItemText';
import Menu from '~/components/core/Atomic/Menu';
import MenuItem from '~/components/core/Atomic/MenuItem';
import FsMenuItem from '~/components/core/FsWrappers/FsMenuItem/FsMenuItem';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { RestrictedPermissions } from '~/components/core/Permissions/RestrictedPermissions';
import type { DocumentFile } from '~/components/Documents/UploadDocumentsCard/UploadAIDocumentsCard';
import MultiUploadDocuments from '~/components/Documents/UploadDocumentsCard/UploadAIDocumentsCard';
import UploadClaimDocumentsWithClive from '~/components/Documents/UploadDocumentsCard/UploadClaimDocumentsWithClive';
import { FileIcon, PlusIcon, UploadIcon } from '~/components/icons';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';
import Divider from '~/components/ui/atoms/Divider';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import colors from '~/theme/tailwind/colors';
import { reportAxiosError } from '~/Utils';
import { getSubOrganizationId } from '~/Utils/ClaimUtils';

interface ChatInputAttachMenuProps {
  claimFeatures: ClaimChatFeatures;
  disabled: boolean;
  onPromptSubmit?: (promptTitle: string, promptKey: string) => Promise<void>;
}

/**
 * The + button and its menu of context documents, uploads and predefined prompts. It reads the claim,
 * so ChatInputV2 mounts it only for a chat that has one.
 */
export const ChatInputAttachMenu: React.FC<ChatInputAttachMenuProps> = ({
  claimFeatures,
  disabled,
  onPromptSubmit,
}) => {
  const { claim } = useClaim({ claimRequired: true });
  const { selectedDocumentIds, setSelectedDocumentIds, predefinedPrompts } = claimFeatures;
  const { trackChatEvent } = useChatMixpanel();
  const { isCliveEnabled } = useCliveClaimContext();
  const subOrganizationId = getSubOrganizationId(claim);
  const isCliveDocumentsUploadEnabled = isCliveEnabled(subOrganizationId);

  const [newDocumentOpen, setNewDocumentOpen] = useState(false);
  const [documentSelectionOpen, setDocumentsSelectionOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = !isNull(menuAnchorEl);

  const sortedPrompts = useMemo(() => {
    return Object.entries(predefinedPrompts).sort(([, a], [, b]) => a.prompt_title.localeCompare(b.prompt_title));
  }, [predefinedPrompts]);

  const onAddDocumentsToChat = (selectedIds: number[]) => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_CONTEXT_DOCUMENTS_ADDED, { document_count: selectedIds.length });
    setSelectedDocumentIds(selectedIds);
    setDocumentsSelectionOpen(false);
  };

  const handlePromptSelect = async (promptKey: string, promptTitle: string) => {
    setMenuAnchorEl(null);
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_PREDEFINED_PROMPT_SELECTED, {
      prompt_key: promptKey,
      prompt_title: promptTitle,
    });

    if (onPromptSubmit) {
      await onPromptSubmit(promptTitle, promptKey);
    }
  };

  const renderPredefinedPrompts = () => {
    if (isEmpty(predefinedPrompts)) return null;

    return [
      <Divider key="divider" />,
      ...sortedPrompts.map(([promptKey, promptData]) => (
        <MenuItem key={promptKey} onClick={() => handlePromptSelect(promptKey, promptData.prompt_title)}>
          <ListItemText primary={promptData.prompt_title} />
        </MenuItem>
      )),
    ];
  };

  return (
    <>
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableScrollLock
        className="max-h-[600px] min-w-[280px]"
      >
        <MenuItem
          onClick={() => {
            trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_ADD_CONTEXT_CLICKED);
            setMenuAnchorEl(null);
            setDocumentsSelectionOpen(true);
          }}
        >
          <FileIcon iconColor="gray" size={16} className="mr-8" />
          <ListItemText primary="Add context" />
        </MenuItem>

        <RestrictedPermissions action={PERMISSION_ACTIONS.CLAIM_DOCUMENT} verb={PERMISSION_VERBS.WRITE}>
          <FsMenuItem
            onClick={() => {
              trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_UPLOAD_FILE_CLICKED);
              setMenuAnchorEl(null);
              setNewDocumentOpen(true);
            }}
          >
            <UploadIcon iconColor="gray" size={16} className="mr-8" />
            <ListItemText primary="Upload file" />
          </FsMenuItem>
        </RestrictedPermissions>

        {renderPredefinedPrompts()}
      </Menu>

      <IconButton
        className="flex shrink-0 items-center justify-center rounded-full bg-blue-100 p-[10px] text-slate-900 hover:bg-blue-200 disabled:cursor-not-allowed disabled:bg-blue-100 disabled:opacity-50"
        onClick={(event) => {
          trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_PLUS_MENU_OPENED);
          setMenuAnchorEl(event.currentTarget);
        }}
        disabled={disabled}
        aria-label="Open menu"
      >
        <PlusIcon iconColor={colors.slate[900]} size={12} />
      </IconButton>

      {newDocumentOpen &&
        (isCliveDocumentsUploadEnabled ? (
          <UploadClaimDocumentsWithClive
            claim={claim}
            onCancel={() => setNewDocumentOpen(false)}
            onSubmitDocument={() => {
              // Not used for Clive flow, but required by interface
            }}
            onCliveUploadSuccess={() => {
              trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_DOCUMENT_UPLOADED, { upload_method: 'clive' });
              setNewDocumentOpen(false);
            }}
            forceCliveEnabled={true}
          />
        ) : (
          <MultiUploadDocuments
            claim={claim}
            onSubmitDocument={async (documents: DocumentFile[]) => {
              try {
                await axios.post(`/api/v1/claims/${claim.id}/ai/auto_doc_upload`, {
                  stored_file_ids: documents.map((doc) => doc.stored_file_id),
                });
                trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_DOCUMENT_UPLOADED, { upload_method: 'standard' });
                setNewDocumentOpen(false);
              } catch (error) {
                await reportAxiosError(error);
              }
            }}
            onCancel={() => setNewDocumentOpen(false)}
          />
        ))}

      {documentSelectionOpen ? (
        <ChatContextDocumentsDialog
          open={documentSelectionOpen}
          onClose={() => setDocumentsSelectionOpen(false)}
          onAddToChat={(selectedIds) => onAddDocumentsToChat(selectedIds)}
          documentIds={selectedDocumentIds}
        />
      ) : null}
    </>
  );
};
